import { useSearchParams } from 'react-router-dom'

import { SALES_GRANULARITIES, type SalesGranularity } from '@/api/competitionDetail'
import { serializeFilters, type GlobalFilters } from '@/lib/filters'
import { useUrlChoice } from '@/lib/useUrlChoice'

import { LIST_RETURN_PARAM, listStateParams } from '../competitions/params'

/*
 * The page's own URL vocabulary, kept beside the global filters so a link opens the same
 * view: the tab (`?tab=sales`), the Sales tab's granularity, and the competitions list's
 * state the back link returns to. The Marketing sources tab's model is the Attribution
 * page's own `?attr_model=` (`useModel`), so a link carries one choice between the two.
 */

/** The tabs, in order (blueprint 10 §10.7). */
export const DETAIL_TABS = ['summary', 'sales', 'funnel', 'sources', 'advertising', 'customers', 'devices', 'timeline'] as const
export type DetailTab = (typeof DETAIL_TABS)[number]

export const DETAIL_TAB_LABELS: Record<DetailTab, string> = {
  summary: 'Summary',
  sales: 'Sales',
  funnel: 'Funnel',
  sources: 'Marketing sources',
  advertising: 'Advertising',
  customers: 'Customers',
  devices: 'Devices',
  timeline: 'Timeline',
}

export const TAB_PARAM = 'tab'

export function useDetailTab(): [DetailTab, (next: DetailTab) => void] {
  return useUrlChoice(TAB_PARAM, DETAIL_TABS, 'summary')
}

export function useSalesGranularity(): [SalesGranularity, (next: SalesGranularity) => void] {
  return useUrlChoice('granularity', SALES_GRANULARITIES, 'day')
}

/**
 * The competitions list as it was when this page was opened from it: the global filters
 * (less this competition, which is the page's entity, not a filter of the list) and the
 * list's own status, category and sort the row's link carried (`LIST_RETURN_PARAM`).
 */
export function backToListHref(filters: GlobalFilters, listState: string | null): string {
  const query = serializeFilters({ ...filters, dimensions: { ...filters.dimensions, competition: undefined } })
  for (const [key, value] of listStateParams(listState)) query.append(key, value)
  const search = query.toString()
  return search ? `/analytics/competitions?${search}` : '/analytics/competitions'
}

/** The back link for the current URL. */
export function useBackToListHref(filters: GlobalFilters): string {
  const [params] = useSearchParams()
  return backToListHref(filters, params.get(LIST_RETURN_PARAM))
}
